import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { ClientMetrics } from '@theraply/api-client';
import { UserGroupIcon, UserPlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

export interface ClientsChartProps {
  data: ClientMetrics;
  loading?: boolean;
}

const COLORS = {
  new: '#10b981', // green
  returning: '#6366f1', // indigo
};

/**
 * Clients Chart Component
 * Displays client acquisition and retention metrics
 */
export function ClientsChart({ data, loading = false }: ClientsChartProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="h-80 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data || data.totalClients === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Client Insights</h3>
        <div className="h-80 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <UserGroupIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
            <p className="text-lg">No client data available</p>
            <p className="text-sm mt-2">Your clients will appear here</p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare pie chart data
  const pieData = [
    {
      name: 'New Clients',
      value: data.newClients,
      fill: COLORS.new,
    },
    {
      name: 'Returning Clients',
      value: data.returningClients,
      fill: COLORS.returning,
    },
  ].filter((item) => item.value > 0);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-6">Client Insights</h3>

      {/* Client Metrics Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <UserGroupIcon className="h-6 w-6 mx-auto mb-2 text-gray-600" />
          <p className="text-2xl font-bold text-gray-900">{data.totalClients}</p>
          <p className="text-sm text-gray-600">Total Clients</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <UserPlusIcon className="h-6 w-6 mx-auto mb-2 text-green-600" />
          <p className="text-2xl font-bold text-green-600">{data.newClients}</p>
          <p className="text-sm text-gray-600">New Clients</p>
        </div>
        <div className="text-center p-4 bg-indigo-50 rounded-lg">
          <ArrowPathIcon className="h-6 w-6 mx-auto mb-2 text-indigo-600" />
          <p className="text-2xl font-bold text-indigo-600">
            {data.retentionRate.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-600">Retention Rate</p>
        </div>
      </div>

      {/* Pie Chart */}
      {pieData.length > 0 && (
        <div className="mb-6">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => (
                  <span className="text-sm text-gray-700">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Clients Table */}
      {data.topClients && data.topClients.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Top Clients</h4>
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Client
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Sessions
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.topClients.map((client, index) => (
                  <tr key={client.consumerId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      Client #{client.consumerId}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900">
                        {client.sessionsInPeriod}
                      </div>
                      <div className="text-xs text-gray-500">
                        of {client.totalSessions} total
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          client.isNewClient
                            ? 'bg-green-100 text-green-800'
                            : 'bg-indigo-100 text-indigo-800'
                        }`}
                      >
                        {client.isNewClient ? 'New' : 'Returning'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientsChart;
